import { Tile } from "../../Tile";
import { Claims, IdCard, Edit, Documents } from "../../../svg";
import { useUrls } from "../../../app/Providers/AppContext";
import { formatDateISO, getPolicyName, PolicyDetails } from "shared";
import { getVehiclesLabel } from "../../../utils/vehicleUtils";
import styles from "./InsuranceTiles.module.scss";
import { TagAction } from "../../../types/Tag.types";
import { useDialog } from "../../Dialog";
import { CompOnlyDialog } from "../CompOnlyDialog";
import { useEffect, useState } from "react";
import { useDownloadIdCard } from "../useDownloadIdCard";
import { useDownloadDocument } from "../useDownloadDocument";
import { getFileClaimUrl } from "../../../utils/urlUtils";
import { DownloadDocumentErrorAlert } from "../DownloadDocumentErrorAlert";
import { routes } from "../../../routing/routes";
import { PopoverTile } from "../../PopoverTile";
import { validateState } from "@hagerty/self-service-components/api/utils";

type InsuranceTilesProps = {
  policy: PolicyDetails;
};

const getIdCardTagActionName = (suffix = ""): string => `Insurance ID${suffix}`;
const decPageTagAction: TagAction = {
  actionName: "Declaration Page",
  actionType: "button",
};
const policyDocumentsTagAction: TagAction = {
  actionName: "Policy documents",
  actionType: "link",
};
const managePolicyTagAction: TagAction = {
  actionName: "Manage policy",
  actionType: "link",
};
const fileClaimTagAction: TagAction = {
  actionType: "link",
  actionName: "File a claim",
};
const claimStatusTagAction: TagAction = {
  actionType: "link",
  actionName: "Claim status",
};

export const InsuranceTiles = ({ policy }: InsuranceTilesProps) => {
  const [redirectIdCardPage, setRedirectIdCardPage] = useState(false);
  const [redirectClaims, setRedirectClaims] = useState(false);
  const [redirectPolicyDocument, setRedirectPolicyDocument] = useState(false);
  const [errorAlert, setErrorAlert] = useState<string | undefined>();
  const urls = useUrls();
  const vehicleNamesLabel = getVehiclesLabel(policy.vehicles);
  const compOnlyDialog = useDialog();
  const isCorePolicy = policy.source === "CORE";
  const { partner, policyNumber, country } = policy;
  const isStateFarm = partner === "state-farm";
  const isIdCardTileVisible = isCorePolicy || policy.hasIdCard;
  const futureVersion = policy.futureVersion;
  const hasFutureVersion = !!futureVersion;
  const showFutureDropdown = hasFutureVersion && policy.hasIdCard;

  const policyName = getPolicyName(
    validateState(policy.stateRegion),
    !isCorePolicy
  );

  const {
    download: downloadIdCard,
    isDownloading: isDownloadingIdCard,
    error: downloadIdCardError,
  } = useDownloadIdCard(policyNumber, country);

  const {
    download: downloadFutureIdCard,
    isDownloading: isDownloadingFutureIdCard,
    error: downloadFutureIdCardError,
  } = useDownloadIdCard(futureVersion?.policyNumber || "", country);

  const {
    download: downloadDecPage,
    isDownloading: isDownloadingDecPage,
    error: downloadDecPageError,
  } = useDownloadDocument(policyNumber, policy.source, "DEC-PAGE");

  const managePolicyUrl = routes.policyDetails(policyNumber);

  const onClickClaimsLink = () => {
    setRedirectClaims(true);
  };

  useEffect(() => {
    if (downloadIdCardError || downloadFutureIdCardError) {
      setErrorAlert("ID Card");
    }
  }, [downloadIdCardError, downloadFutureIdCardError]);

  useEffect(() => {
    if (downloadDecPageError) {
      setErrorAlert("Declaration Page");
    }
  }, [downloadDecPageError]);

  return (
    <>
      <div className={styles.policyTiles}>
        {isIdCardTileVisible && (
          <>
            {!isStateFarm ? (
              <Tile
                icon={
                  <img src={IdCard} alt="Insurance ID" aria-hidden="true" />
                }
                className={styles.tile}
                cta="Insurance ID"
                ariaLabel={`Access Insurance ID for your ${vehicleNamesLabel} on policy ${policyNumber}`}
                tagAction={{
                  actionName: getIdCardTagActionName(),
                  actionType: "link",
                }}
                href={policy.hasIdCard ? urls.insurance.idCardPage : undefined}
                onClick={() =>
                  policy.hasIdCard
                    ? setRedirectIdCardPage(true)
                    : compOnlyDialog.open()
                }
                isLoading={redirectIdCardPage}
              />
            ) : (
              <PopoverTile
                icon={
                  <img src={IdCard} alt="Insurance ID" aria-hidden="true" />
                }
                isLoading={isDownloadingIdCard || isDownloadingFutureIdCard}
                className={styles.tile}
                cta="Insurance ID"
                ariaLabel={`Access Insurance ID for your ${vehicleNamesLabel} on policy ${policyNumber}`}
                tagAction={
                  showFutureDropdown
                    ? undefined
                    : {
                        actionName: getIdCardTagActionName(),
                        actionType: "button",
                      }
                }
                onClick={() => {
                  setErrorAlert(undefined);
                  policy.hasIdCard ? downloadIdCard() : compOnlyDialog.open();
                }}
                ctaList={
                  showFutureDropdown
                    ? [
                        {
                          tagAction: {
                            actionName: getIdCardTagActionName(" - Current"),
                            actionType: "button",
                          },
                          onClick: downloadIdCard,
                          label: `Current: ${formatDateISO(
                            policy.effectiveDate
                          )} - ${formatDateISO(policy.expirationDate)}`,
                        },
                        {
                          tagAction: {
                            actionName: getIdCardTagActionName(" - Future"),
                            actionType: "button",
                          },
                          onClick: downloadFutureIdCard,
                          label: `Future: ${formatDateISO(
                            futureVersion.effectiveDate
                          )} - ${formatDateISO(futureVersion.expirationDate)}`,
                        },
                      ]
                    : undefined
                }
              />
            )}
          </>
        )}

        {!isCorePolicy && (
          <Tile
            icon={
              <img src={Documents} alt="Declaration page" aria-hidden="true" />
            }
            isLoading={isDownloadingDecPage}
            className={styles.tile}
            cta="Declaration Page"
            ariaLabel={`Declaration page for policy ${policyNumber}`}
            tagAction={decPageTagAction}
            onClick={() => {
              setErrorAlert(undefined);
              downloadDecPage();
            }}
          />
        )}

        {isCorePolicy && (
          <>
            {country === "USA" && (
              <PopoverTile
                icon={
                  <img src={Claims} alt="Manage claims" aria-hidden="true" />
                }
                className={styles.tile}
                cta="Claims"
                ariaLabel={`Manage claims for your ${vehicleNamesLabel} on policy ${policyNumber}`}
                isLoading={redirectClaims}
                ctaList={[
                  {
                    tagAction: fileClaimTagAction,
                    onClick: onClickClaimsLink,
                    href: getFileClaimUrl(policy, urls),
                    label: "File a claim",
                  },
                  {
                    tagAction: claimStatusTagAction,
                    onClick: onClickClaimsLink,
                    href: urls.insurance.fileClaim(policyNumber, country),
                    label: "Claim status",
                  },
                ]}
              />
            )}
            {country === "CANADA" && (
              <Tile
                icon={
                  <img src={Claims} alt="File a claim" aria-hidden="true" />
                }
                className={styles.tile}
                cta="File a claim"
                ariaLabel={`File a claim for your ${vehicleNamesLabel} on policy ${policyNumber}`}
                href={getFileClaimUrl(policy, urls)}
                tagAction={fileClaimTagAction}
                onClick={onClickClaimsLink}
                isLoading={redirectClaims}
              />
            )}

            <Tile
              icon={
                <img
                  src={Documents}
                  alt="Policy documents"
                  aria-hidden="true"
                />
              }
              className={styles.tile}
              cta="Policy documents"
              ariaLabel={`Policy documents for policy ${policyNumber}`}
              href={urls.insurance.viewPolicy(
                policyNumber,
                country,
                isStateFarm
              )}
              tagAction={policyDocumentsTagAction}
              onClick={() => setRedirectPolicyDocument(true)}
              isLoading={redirectPolicyDocument}
            />

            {!isStateFarm && (
              <Tile
                icon={<img src={Edit} alt="Manage policy" aria-hidden="true" />}
                className={styles.tile}
                cta="Manage policy"
                ariaLabel={`Manage policy ${policyNumber} for your ${vehicleNamesLabel}`}
                href={managePolicyUrl}
                tagAction={managePolicyTagAction}
              />
            )}
          </>
        )}
      </div>

      {compOnlyDialog.isOpen && (
        <CompOnlyDialog
          policyName={policyName}
          compOnlyType={policy.compOnlyType ?? "STANDARD"}
          managePolicyUrl={
            isCorePolicy && !isStateFarm && policy.compOnlyType !== "EXCESS_TPL"
              ? managePolicyUrl
              : ""
          }
          onClose={compOnlyDialog.close}
        />
      )}

      {errorAlert && (
        <DownloadDocumentErrorAlert
          documentName={errorAlert}
          onClose={() => setErrorAlert(undefined)}
        />
      )}
    </>
  );
};
